"use client";

import React, { Fragment, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { Play } from "schema";

import { trpc } from "~/app/providers";
import FriendSelect from "../../../_components/FriendSelect";
import InviteFriendModal from "../../../_components/InviteFriendModal";

interface Character {
  id?: number;
  userId: string | null;
  name: string;
  playId: number;
  email: string | null;
}
export default function EditPlayModal({
  play,
  characters,
  users,
}: {
  play: Play;
  characters: Character[];
  users: {
    id: string;
    handle: string | null;
    name: string | null;
    avatar: {
      colors: number;
      name: string;
      variant:
        | "marble"
        | "beam"
        | "pixel"
        | "sunset"
        | "ring"
        | "bauhaus"
        | undefined;
    } | null;
  }[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    playData: play,
    playCharacters: characters,
  });

  const initialFormDataRef = useRef<{
    playData: { userId: string; id: number; title: string };
    playCharacters: Character[];
  }>();

  // Capture the initial state once on component mount
  useEffect(() => {
    initialFormDataRef.current = {
      playData: play,
      playCharacters: characters,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCharacter = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      playCharacters: [
        ...prevFormData.playCharacters,
        {
          userId: null,
          name: "",
          playId: play.id,
          email: "",
        },
      ],
    }));
  };

  // const removeCharacter = (index: number) => {
  //   setFormData((prevFormData) => ({
  //     ...prevFormData,
  //     playCharacters: prevFormData.playCharacters.filter(
  //       (item, idx) => idx !== index,
  //     ),
  //   }));
  // };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      playData: { ...prevFormData.playData, title: newValue },
    }));
  };

  const assignEmail = (index: number, email: string) => {
    setFormData((prevState) => {
      const updatedCharacters = prevState.playCharacters.map(
        (character, idx) =>
          idx === index ? { ...character, email, userId: "" } : character,
      );
      return { ...prevState, playCharacters: updatedCharacters };
    });
  };

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const newValue = event.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      playCharacters: prevFormData.playCharacters.map((item, idx) => {
        if (idx === index) {
          return { ...item, name: newValue };
        }
        return item;
      }),
    }));
  };

  const handleUserIdChange = (
    value: {
      id: string;
      handle: string | null;
      name: string | null;
      avatar: {
        colors: number;
        name: string;
        variant:
          | "marble"
          | "beam"
          | "pixel"
          | "sunset"
          | "ring"
          | "bauhaus"
          | undefined;
      } | null;
    },
    index: number,
  ) => {
    if (typeof value === "object" && value !== null) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        playCharacters: prevFormData.playCharacters.map((item, idx) => {
          if (idx === index) {
            return { ...item, userId: value.id, email: "" };
          }
          return item;
        }),
      }));
    }
  };

  const router = useRouter();

  const updatePlay = trpc.plays.updatePlay.useMutation({
    onSuccess: () => {
      router.refresh();
      closeModal();
    },
  });

  function closeModal() {
    setIsOpen(false);
  }

  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <button
        className="flex gap-1 p-2 text-sm font-medium text-gray-600  hover:text-gray-900"
        onClick={openModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="h-6 w-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
          />
        </svg>
        Edit Cast
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform  rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mx-20 flex flex-col items-center">
                    <>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        Edit Play
                      </h3>
                      <div className="mt-2 w-full">
                        <label className="relative block rounded-md border border-gray-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-300">
                          <input
                            type="text"
                            defaultValue={play.title}
                            placeholder="Title"
                            onChange={(e) => handleInput(e)}
                            className="border-none bg-transparent placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0"
                          />

                          <span className="pointer-events-none absolute start-2.5 top-0 -translate-y-1/2 bg-white p-0.5 text-xs text-gray-700 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-0 peer-focus:text-xs">
                            Title
                          </span>
                        </label>
                      </div>
                    </>

                    <>
                      <h3 className="mt-10 text-lg font-medium leading-6 text-gray-900">
                        Edit Cast
                      </h3>
                      <div className="mt-4 space-y-3">
                        {formData.playCharacters.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center space-x-2"
                          >
                            <label className="relative block rounded-md border border-gray-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-300">
                              <input
                                type="text"
                                defaultValue={item.name}
                                onChange={(e) => handleNameChange(e, idx)}
                                className="border-none bg-transparent placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0"
                              />

                              <span className="pointer-events-none absolute start-2.5 top-0 -translate-y-1/2 bg-white p-0.5 text-xs text-gray-700 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-0 peer-focus:text-xs">
                                Character {idx + 1}
                              </span>
                            </label>
                            {item.email ? (
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-md italic text-gray-700">
                                  {item.email}
                                </p>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="size-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                                  />
                                </svg>
                                <div className="flex justify-end">
                                  <InviteFriendModal
                                    row={item}
                                    updateRow={assignEmail}
                                    idx={idx}
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                                <FriendSelect
                                  onChange={handleUserIdChange}
                                  index={idx}
                                  users={users}
                                  actor={users.find(
                                    (user) => user.id === item.userId,
                                  )}
                                />
                                <InviteFriendModal
                                  row={item}
                                  updateRow={assignEmail}
                                  idx={idx}
                                />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                    <>
                      <div className="mt-12 flex items-center space-x-5">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={addCharacter}
                        >
                          Add additional character
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          onClick={() => {
                            updatePlay.mutate({
                              play: formData.playData,
                              characters: formData.playCharacters,
                            });
                          }}
                        >
                          Update
                        </button>
                      </div>
                    </>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
